/**
 * Responsibility: the figures across the top of the collection.
 *
 * Size is two numbers, not one. A collection of one card times forty is not
 * a collection of forty cards, and showing only the second would flatter it.
 *
 * Movement is the honest part. Nothing free backfills a collection's past,
 * so the series starts the day the first card is added and the windows read
 * "not yet" rather than "0%" until there is enough of it. A zero there would
 * be a claim - that the collection held its value - rather than an absence.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { MetricCard } from '@kit/components';
import type { CollectionSummary } from '../../api/client';
import { money, percent, toNumber } from '../grading/money';

function Movement({ label, value, days }: { label: string; value: string | null; days: number }) {
  const ratio = toNumber(value);
  if (ratio === null) {
    return (
      <Stack spacing={0} alignItems="flex-start">
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          not yet
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={0} alignItems="flex-start">
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
        color={ratio >= 0 ? 'success.main' : 'error.main'}
      >
        {ratio >= 0 ? '+' : ''}
        {percent(value, 1)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        over {days}d
      </Typography>
    </Stack>
  );
}

type CollectionStatsProps = {
  summary: CollectionSummary;
  /**
   * The page's own controls, carried on the movement bar's spare right
   * side rather than on a row of their own. Everything above the grid
   * competes with the grid for height, and the bar had the room.
   */
  actions?: ReactNode;
};

export function CollectionStats({ summary, actions }: CollectionStatsProps) {
  const gain = toNumber(summary.total_gain);
  const tracked = summary.tracked_days;

  return (
    <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        }}
      >
        <MetricCard
          label="Collection size"
          value={String(summary.card_count)}
          helper={`${summary.item_count} ${summary.item_count === 1 ? 'position' : 'positions'}`}
        />
        <MetricCard
          label="Collection value"
          value={money(summary.total_value)}
          helper="at the grade held"
        />
        <MetricCard
          label="Cost basis"
          value={summary.total_cost === '0' ? '—' : money(summary.total_cost)}
          helper="where recorded"
        />
        <MetricCard
          label="Unrealised"
          value={gain === null ? '—' : money(summary.total_gain)}
          helper={gain === null ? 'no costs recorded' : gain >= 0 ? 'up on cost' : 'down on cost'}
        />
      </Box>

      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        sx={{
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: 'action.hover',
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ minWidth: 72 }}>
          Movement
        </Typography>
        <Movement label="Day" value={summary.change_1d} days={1} />
        <Movement label="Month" value={summary.change_30d} days={30} />
        <Movement label="Quarter" value={summary.change_90d} days={90} />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {tracked === 0
            ? 'no readings yet'
            : `${tracked} ${tracked === 1 ? 'day' : 'days'} tracked`}
        </Typography>
        {actions}
      </Stack>

    </Stack>
  );
}
